/**
 * The Root container
 */
import {
  useAccount,
  useAuthorization,
} from "@bkry/bowline-components/CustomHook";
import EmailConfirmationModal from "@bkry/bowline-components/EmailConfirmationModal";
import { FooterLayout } from "@bkry/bowline-components/FooterLayout";
import { HeaderLayout } from "@bkry/bowline-components/HeaderLayout";
import { ProjectOnboarding } from "@bkry/bowline-components/ProjectOnboarding";
import { WalletChangeModal } from "@bkry/bowline-components/WalletChangeModal";
import { googleWallet } from "@bkry/bowline-connectors";
import { isServer } from "@bkry/bowline-redux";
import {
  getProjectAttributes,
  showProject,
} from "@bkry/bowline-redux/projects";
import {
  getWalletAttributes,
  setKeepWalletConnection,
  setSignInToFalse,
  showWallet,
  updateWallet,
} from "@bkry/bowline-redux/wallets";
import { getEnv, keepWalletConnected } from "@bkry/bowline-utils";
import { useRegisterEffect, useSsrEffect, useSsrState } from "@issr/core";
import { useWeb3React } from "@web3-react/core";
import classNames from "classnames";
import { sha256 } from "js-sha256";
import React from "react";
import Loadable from "react-loadable";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import { StickyContainer } from "react-sticky";
import Cookies from "universal-cookie";
import ProjectDesignLoader from "../components/ProjectDesignLoader";
import PasswordUnlockForm from "../components/utils/PasswordUnlockForm";
import FourOFour from "./FourOFour";
import LicenseAlert from "./utils/LicenseAlert";
import { setStatus } from "@bkry/bowline-redux/authConnect";

const Dashboard = Loadable({
  loader: () => import(/* webpackChunkName: "Dashboard" */ "./Dashboard"),
  loading: () => null,
  modules: ["Dashboard"],
});

const FourOThree = Loadable({
  loader: () => import(/* webpackChunkName: "FourOThree" */ "./FourOThree"),
  loading: () => null,
  modules: ["FourOThree"],
});

const Whitelist = Loadable({
  loader: () => import(/* webpackChunkName: "Whitelist" */ "./Whitelist"),
  loading: () => null,
  modules: ["Whitelist"],
});

const EventsRoot = Loadable({
  loader: () =>
    import(/* webpackChunkName: "EventsRoot" */ "./Events/EventsRoot"),
  loading: () => null,
  modules: ["EventsRoot"],
});

const RafflesRoot = Loadable({
  loader: () =>
    import(/* webpackChunkName: "RafflesRoot" */ "./Raffles/RafflesRoot"),
  loading: () => null,
  modules: ["RafflesRoot"],
});

const UnlockableContentsRoot = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "UnlockableContentsRoot" */ "./UnlockableContents/UnlockableContentsRoot"
    ),
  loading: () => null,
  modules: ["UnlockableContentsRoot"],
});

const VotingsRoot = Loadable({
  loader: () =>
    import(/* webpackChunkName: "VotingsRoot" */ "./Votings/VotingsRoot"),
  loading: () => null,
  modules: ["Votings"],
});

const PartnerDealsRoot = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "PartnerDeals" */ "./PartnerDeals/PartnerDealsRoot"
    ),
  loading: () => null,
  modules: ["PartnerDeals"],
});

const TokensRoot = Loadable({
  loader: () =>
    import(/* webpackChunkName: "TokensRoot" */ "./Tokens/TokensRoot"),
  loading: () => null,
  modules: ["TokensRoot"],
});

const StaticPage = Loadable({
  loader: () => import(/* webpackChunkName: "StaticPage" */ "./StaticPage"),
  loading: () => null,
  modules: ["StaticPage"],
});

const MintingSectionRoot = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "MintingSection" */ "./Minting/MintingSectionRoot"
    ),
  loading: () => null,
  modules: ["MintingSection"],
});

const Roadmap = Loadable({
  loader: () => import(/* webpackChunkName: "Roadmap" */ "./Roadmap"),
  loading: () => null,
  modules: ["Roadmap"],
});

const CommunityAnnouncementsRoot = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "CommunityAnnouncements" */ "./CommunityAnnouncements/CommunityAnnouncementsRoot"
    ),
  loading: () => null,
  modules: ["CommunityAnnouncements"],
});

// DON'T DELETE THE FOLLOWING COMMENT: NEEDED FOR UPDATING THIS FILE WITH THE SCAFFOLD SCRIPT
// new scaffolded loadable imports

/**
 * The Root container used as entry point for the react-router.
 * All routes are lazy-loaded with Loadable.
 */

function Root() {
  const REACT_APP_PROJECT_ID = getEnv("REACT_APP_PROJECT_ID");
  const REACT_APP_DASHBOARD_REDIRECT = getEnv("REACT_APP_DASHBOARD_REDIRECT");

  const [unlocked, setUnlocked] = useSsrState(false);

  const cookieHandler = new Cookies();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const registerEffect = useRegisterEffect();
  const { connector, ...rest } = useWeb3React();
  const { account } = useAccount();
  const { isBlocked, currentAccessMode } = useAuthorization();
  const signedIn = useSelector(
    (state) => state.wallets.siweSuccess || state.wallets.siwsSuccess
  );

  const keepWalletConnection = useSelector(
    (state) => state.wallets.keepWalletConnection
  );

  const suggestedChainOptions = useSelector(
    (state) => state.projects.defaultNetworks[REACT_APP_PROJECT_ID]
  );

  const walletData = useSelector((state) =>
    getWalletAttributes(state, account)
  );

  const projectData = useSelector((state) =>
    getProjectAttributes(state, REACT_APP_PROJECT_ID)
  );

  const defaultAvailableLocales = useSelector(
    (state) => state.formLocales.defaultAvailableLocales
  );

  const currentLocale = useSelector((state) => state.i18nState.lang);

  const urlParams = new URLSearchParams(history.location.hash);
  const sessionId = urlParams.get("#sessionId");
  const web3AuthRedirectParams = urlParams.get("#b64Params");
  const languageOptions =
    projectData?.available_languages || defaultAvailableLocales;
  const defaultLanguage = "en";
  const urlLocale = location.pathname.split("/")?.[1];
  const containsAvailableLanguageParam = languageOptions?.includes(urlLocale);
  const isEmailConfirmationModalOpen =
    !walletData?.email_confirmation_dismissed_last_login &&
    walletData?.email_masked &&
    !walletData?.confirmed;

  useSsrEffect(() => {
    if (!account) return;

    registerEffect(
      dispatch,
      showWallet({
        walletAddress: account,
        projectId: REACT_APP_PROJECT_ID,
      })
    );
  }, [account]);

  useSsrEffect(() => {
    if (web3AuthRedirectParams && !account && suggestedChainOptions) {
      try {
        const decodedParamsJson = atob(web3AuthRedirectParams);
        const decodedParams = JSON.parse(decodedParamsJson);
        if (decodedParams.loginParams) {
          handleConnect();
        }
      } catch (e) {
        console.error("Error decoding web3AuthRedirectParams", e);
      }
    }
  }, [web3AuthRedirectParams, account, suggestedChainOptions]);

  useSsrEffect(() => {
    if (!suggestedChainOptions || web3AuthRedirectParams) return;
    if (keepWalletConnection && !account) {
      keepWalletConnected(account, null, connector, {
        ...rest,
        ...buildWeb3AuthOptions(),
      });
    }
  }, [account, suggestedChainOptions, web3AuthRedirectParams]);

  if (!isServer) window.account = account;

  const loadedProjectId = useSelector((state) => state.projects.showId);

  useSsrEffect(() => {
    registerEffect(dispatch, showProject(REACT_APP_PROJECT_ID));
  }, [dispatch, REACT_APP_PROJECT_ID]);

  useSsrEffect(() => {
    if (!isServer && sessionId && loadedProjectId) {
      handleConnect();
    }
  }, [sessionId, projectData, isServer]);

  useSsrEffect(() => {
    const accessKey = cookieHandler.get("access_key") || "";

    if (sha256(accessKey)?.toString() === projectData?.access_key) {
      setUnlocked(true);
    }
  }, [projectData?.access_key]);

  useSsrEffect(() => {
    const walletToken = cookieHandler.get("wallet-token");

    if (walletToken || !signedIn) return;

    registerEffect(dispatch, setSignInToFalse());
  }, []);
  const connectionStatus = useSelector((state) => state.authConnect.status);
  const setConnectionStatus = (status) => dispatch(setStatus(status));

  const buildWeb3AuthOptions = () => {
    const options = {
      clientId: suggestedChainOptions?.wta_id,
      authConnectionId: suggestedChainOptions?.wta_google_verifier,
      setStatus: setConnectionStatus,
      loginMethods: suggestedChainOptions?.wta_google_verifier
        ? {
            google: {
              name: "google login",
              authConnectionId: suggestedChainOptions?.wta_google_verifier,
            },
          }
        : undefined,
    };
    return options;
  };

  const handleConnect = () => {
    if (connectionStatus === "starting") return;
    setConnectionStatus("starting");
    googleWallet
      .setOptions(buildWeb3AuthOptions())
      .then(() => googleWallet.activate())
      .then(() => {
        dispatch(
          setKeepWalletConnection({
            keepWalletConnection: true,
            lastProvider: "wta-google",
          })
        );
        history.replace({
          pathname: location.pathname,
          search: location.search,
          hash: "",
        });
      })
      .catch((err) => {
        console.error(err);
        setConnectionStatus("");
      });
  };

  const languageRedirectCheck = () => {
    // check if the current path contains a language param which is supported
    if (!projectData) return null;
    if (containsAvailableLanguageParam) return null;

    // const isUserLanguageSupported = languageOptions?.includes(currentUser?.locale);
    // const isLanguageSupported = languageOptions?.includes(language);
    const redirectLanguage = currentLocale || defaultLanguage;
    if (
      languageOptions &&
      !location.pathname.startsWith(`/${redirectLanguage}/`)
    ) {
      return (
        <Redirect
          to={{
            pathname: `/${redirectLanguage}${location.pathname}`,
            search: location.search,
            state: {
              accessMode: currentAccessMode || projectData?.access_mode,
              accessErrorCode: 403,
            },
          }}
        />
      );
    }
    return null;
  };

  const emailConfirmationModalClose = () => {
    registerEffect(
      dispatch,
      updateWallet({
        projectId: REACT_APP_PROJECT_ID,
        address: walletData?.address,
        wallet: {
          email_confirmation_dismissed_last_login: true,
        },
      })
    );
  };

  return (
    <>
      {isEmailConfirmationModalOpen && (
        <EmailConfirmationModal
          isOpen={isEmailConfirmationModalOpen}
          toggle={emailConfirmationModalClose}
        />
      )}
      <ProjectDesignLoader />
      <StickyContainer id="Root">
        {projectData?.access_mode === "password" && !unlocked ? (
          <PasswordUnlockForm setUnlocked={setUnlocked} />
        ) : (
          <>
            <HeaderLayout />
            {account && (
              <>
                <ProjectOnboarding />
                <WalletChangeModal />
              </>
            )}
            <div
              id={"content"}
              className={classNames(
                {
                  "content--with-sidebar":
                    projectData?.header_layout === "sidebar",
                },
                {
                  "content--with-floating_sidebar":
                    projectData?.header_layout === "floating_sidebar",
                }
              )}
            >
              <LicenseAlert />
              <Switch>
                {REACT_APP_PROJECT_ID === "UNKOWN" && <Redirect to="/404" />}
                {languageRedirectCheck()}
                {REACT_APP_DASHBOARD_REDIRECT && (
                  <Route path="/:locale/" exact>
                    <Redirect to={{ pathname: REACT_APP_DASHBOARD_REDIRECT }} />
                  </Route>
                )}
                {!REACT_APP_DASHBOARD_REDIRECT && (
                  <Route path="/:locale/" exact component={Dashboard} />
                )}
                <Route path="/:locale/403" component={FourOThree} />
                {isBlocked && (
                  <Redirect
                    to={{
                      pathname: "/403",
                      search: location.search,
                      state: {
                        accessMode:
                          currentAccessMode || projectData?.access_mode,
                        accessErrorCode: 403,
                      },
                    }}
                  />
                )}
                <Route path={"/:locale/allowlist"} component={Whitelist} />
                <Route
                  path="/:locale/contents"
                  component={UnlockableContentsRoot}
                />
                <Route path="/:locale/events" component={EventsRoot} />
                <Route path="/:locale/raffles" component={RafflesRoot} />
                <Route path={"/:locale/votings"} component={VotingsRoot} />
                <Route path="/:locale/deals" component={PartnerDealsRoot} />
                <Route path="/:locale/tokens" component={TokensRoot} />
                <Route path="/:locale/p/*" component={StaticPage} />
                <Route path="/:locale/minting" component={MintingSectionRoot} />
                <Route path="/:locale/r/:roadmapSlug" component={Roadmap} />
                <Route
                  path="/:locale/announcements"
                  component={CommunityAnnouncementsRoot}
                />

                {/* // DON'T DELETE THE FOLLOWING COMMENT: NEEDED FOR UPDATING THIS FILE WITH THE SCAFFOLD SCRIPT */}
                {/* new routes */}

                <Route component={FourOFour} />
              </Switch>
            </div>
            {/* <WalletModalProvider>
              <WalletMultiButton />
            </WalletModalProvider> */}
            <FooterLayout />
          </>
        )}
      </StickyContainer>
    </>
  );
}

/** export the redux connected component and add a frontload call */
export default withRouter(Root);
